import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    LayoutDashboard, ClipboardList, ShoppingBag, TrendingUp,
    CheckCircle, Clock, Star, MapPin, AlertCircle, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useAuth } from '../App';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';

const SalesmanDashboard = () => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [recentAssignments, setRecentAssignments] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, assignmentsRes, ordersRes] = await Promise.all([
                axios.get(`${API}/api/salesman/stats`, { headers }),
                axios.get(`${API}/api/salesman/assignments`, { headers }),
                axios.get(`${API}/api/salesman/orders`, { headers }),
            ]);
            setStats(statsRes.data);
            setRecentAssignments(assignmentsRes.data.slice(0, 5));
            setRecentOrders(ordersRes.data.slice(0, 5));
        } catch (e) {
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const statusColor = (status) => {
        const map = {
            pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30',
            scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30',
            in_progress: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30',
            completed: 'bg-green-100 text-green-700 dark:bg-green-900/30',
            cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30',
        };
        return map[status] || 'bg-gray-100 text-gray-700';
    };

    if (loading) return (
        <DashboardLayout userRole="salesman">
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
            </div>
        </DashboardLayout>
    );

    const pendingCount = recentAssignments.filter(a => !a.salesman_accepted && a.salesman_accepted !== false).length;

    return (
        <DashboardLayout userRole="salesman">
            <div className="space-y-6">
                {/* Welcome Banner */}
                <div className="relative overflow-hidden bg-gradient-to-r from-primary via-primary/90 to-orange-500 rounded-2xl p-6 text-white">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="relative z-10">
                        <p className="text-white/70 text-sm">Welcome back,</p>
                        <h1 className="text-2xl font-bold mt-1">{user?.name} 👋</h1>
                        <p className="text-white/80 text-sm mt-2">Field Sales Agent · SolarSavers</p>
                        {pendingCount > 0 && (
                            <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-medium">
                                <AlertCircle className="w-4 h-4 text-yellow-300" />
                                You have {pendingCount} new assignment{pendingCount > 1 ? 's' : ''} awaiting response
                            </div>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                        { label: 'Assigned', value: stats?.total_assigned || 0, icon: ClipboardList, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                        { label: 'Pending Reply', value: stats?.pending_response || 0, icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
                        { label: 'Accepted', value: stats?.accepted || 0, icon: CheckCircle, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
                        { label: 'Completed', value: stats?.completed || 0, icon: Star, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
                        { label: 'Orders Placed', value: stats?.orders_placed || 0, icon: ShoppingBag, color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20' },
                        { label: 'Revenue', value: `₹${(stats?.revenue_generated || 0).toLocaleString('en-IN')}`, icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
                    ].map((s, i) => (
                        <div key={i} className="bg-card border rounded-xl p-4">
                            <div className={`w-9 h-9 ${s.bg} rounded-lg flex items-center justify-center mb-2`}>
                                <s.icon className={`w-5 h-5 ${s.color}`} />
                            </div>
                            <p className="text-xl font-bold">{s.value}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                        </div>
                    ))}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {/* Recent Assignments */}
                    <div className="bg-card border rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <h2 className="font-semibold flex items-center gap-2">
                                <ClipboardList className="w-4 h-4 text-primary" />
                                Recent Assignments
                            </h2>
                            <Button size="sm" variant="ghost" onClick={() => navigate('/salesman/assignments')}>View All</Button>
                        </div>
                        {recentAssignments.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">No assignments yet</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {recentAssignments.map(a => (
                                    <div key={a.id} className="px-5 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                                        <div>
                                            <p className="font-medium text-sm">{a.name}</p>
                                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                                <MapPin className="w-3 h-3" />{a.city}, {a.pincode}
                                            </p>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(a.status)}`}>
                                            {a.salesman_accepted === null || a.salesman_accepted === undefined
                                                ? 'New'
                                                : a.status.replace(/_/g, ' ')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Recent Orders */}
                    <div className="bg-card border rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <h2 className="font-semibold flex items-center gap-2">
                                <ShoppingBag className="w-4 h-4 text-primary" />
                                Orders I Placed
                            </h2>
                            <Button size="sm" variant="ghost" onClick={() => navigate('/salesman/place-order')}>Place Order</Button>
                        </div>
                        {recentOrders.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">No orders placed yet</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {recentOrders.map(o => (
                                    <div key={o.id} className="px-5 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                                        <div>
                                            <p className="font-medium text-sm">
                                                {o.customer?.name || o.customer_name_override || 'Customer'}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                ₹{o.total_amount?.toLocaleString('en-IN')} · {o.items?.length} item(s)
                                            </p>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(o.status)}`}>
                                            {o.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                        { label: 'My Assignments', desc: 'View & respond to visits', icon: ClipboardList, path: '/salesman/assignments', color: 'from-blue-500 to-blue-600' },
                        { label: 'Place Order', desc: 'Order for a customer', icon: ShoppingBag, path: '/salesman/place-order', color: 'from-orange-500 to-orange-600' },
                        { label: 'My Orders', desc: 'View orders I placed', icon: TrendingUp, path: '/salesman/orders', color: 'from-green-500 to-green-600' },
                    ].map((item, i) => (
                        <button
                            key={i}
                            onClick={() => navigate(item.path)}
                            className={`bg-gradient-to-br ${item.color} text-white rounded-xl p-5 text-left hover:opacity-90 transition-all active:scale-95 shadow-lg`}
                        >
                            <item.icon className="w-7 h-7 mb-3 opacity-90" />
                            <p className="font-bold">{item.label}</p>
                            <p className="text-xs text-white/75 mt-1">{item.desc}</p>
                        </button>
                    ))}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default SalesmanDashboard;
